import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Sparkle, TrendUp, Lightbulb, ArrowsCounterClockwise } from '@phosphor-icons/react';

const API = `/api`;

const PredictiveLeads = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchPredictions();
  }, []);

  const fetchPredictions = async (refresh = false) => {
    if (refresh) setRefreshing(true);
    try {
      const { data: predData } = await axios.get(`${API}/ai/predict`, {
        withCredentials: true,
      });
      setData(predData);
    } catch (error) {
      console.error('Error fetching predictions:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex flex-col items-center justify-center py-12 gap-4">
          <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
          <div className="text-center">
            <p className="text-sm font-medium text-slate-900">IA Claude analisando leads...</p>
            <p className="text-xs text-slate-500 mt-1">Calculando probabilidades de conversão</p>
          </div>
        </div>
      </div>
    );
  }

  const getProbColor = (prob) => {
    if (prob >= 75) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (prob >= 50) return 'bg-blue-50 text-blue-700 border-blue-200';
    if (prob >= 25) return 'bg-orange-50 text-orange-700 border-orange-200';
    return 'bg-slate-50 text-slate-700 border-slate-200';
  };

  const getProbBar = (prob) => {
    if (prob >= 75) return 'bg-emerald-500';
    if (prob >= 50) return 'bg-blue-500';
    if (prob >= 25) return 'bg-orange-500';
    return 'bg-slate-400';
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="predictive-leads-card">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
            <Sparkle size={20} weight="fill" className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Predição de Conversão por IA</h3>
            <p className="text-xs text-slate-500">Top leads com maior probabilidade de fechamento</p>
          </div>
        </div>
        <button
          onClick={() => fetchPredictions(true)}
          disabled={refreshing}
          className="px-3 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-2"
          data-testid="refresh-predictions-button"
        >
          <ArrowsCounterClockwise
            size={16}
            className={refreshing ? 'animate-spin' : ''}
          />
          Atualizar
        </button>
      </div>

      {data?.insights && (
        <div className="mb-5 p-4 bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg border border-purple-200">
          <div className="flex items-start gap-3">
            <Lightbulb size={20} weight="duotone" className="text-purple-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-purple-900 mb-1">Insight Estratégico</p>
              <p className="text-sm text-slate-700 leading-relaxed">{data.insights}</p>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3 mb-5">
        {(data?.predictions || []).map((pred, index) => (
          <div
            key={pred.lead_id || index}
            className="p-4 bg-slate-50 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors"
            data-testid={`prediction-item-${index}`}
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-3">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                    index === 0
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {index + 1}º
                </div>
                <div>
                  <p className="font-medium text-slate-900">{pred.name}</p>
                  <p className="text-xs text-slate-500">
                    {pred.enterprise || 'Sem empreendimento'} · Score: {pred.score || 'N/A'}
                  </p>
                </div>
              </div>
              <span
                className={`text-xs px-3 py-1 rounded-full font-bold border ${getProbColor(
                  pred.probability
                )}`}
              >
                {pred.probability}% prob.
              </span>
            </div>
            <div className="mt-3">
              <div className="bg-slate-200 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-2 rounded-full transition-all duration-500 ${getProbBar(
                    pred.probability
                  )}`}
                  style={{ width: `${pred.probability}%` }}
                ></div>
              </div>
            </div>
            {pred.reason && (
              <p className="text-xs text-slate-600 mt-2 italic">« {pred.reason} »</p>
            )}
          </div>
        ))}
      </div>

      {data?.recommendations && data.recommendations.length > 0 && (
        <div className="pt-4 border-t border-slate-200">
          <p className="text-sm font-medium text-slate-900 mb-3 flex items-center gap-2">
            <TrendUp size={16} weight="duotone" className="text-emerald-600" />
            Recomendações para o time
          </p>
          <ul className="space-y-2">
            {data.recommendations.map((rec, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-slate-700">
                <span className="text-emerald-600 mt-0.5">✓</span>
                {rec}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default PredictiveLeads;
